import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../app/http-common';
import { clientMetrics, LS_FAVOURITES, LS_FAVOURITE_BUCKETS } from '../constants';

const readLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const writeLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / private-mode errors */
  }
};

// Groups the flat bucket list from GET /publisher-buckets into clients
// ("publishers" in the spec). Each group is { clientId, clientName, buckets, metrics }.
function groupByClient(buckets) {
  const map = new Map();
  for (const b of buckets) {
    const client = b.clientId || {};
    const id = (client._id || 'unassigned').toString();
    const name = client.name || 'Unassigned';
    if (!map.has(id)) map.set(id, { clientId: id, clientName: name, buckets: [] });
    map.get(id).buckets.push(b);
  }
  const groups = [...map.values()];
  for (const g of groups) {
    g.buckets.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    g.metrics = clientMetrics(g.buckets);
  }
  return groups;
}

/**
 * Loads and groups publisher buckets, and owns favourites (per administrator,
 * persisted locally). Reused by Publisher Buckets and the Target buckets panel.
 *
 * Spec 2.5: opening a bucket must retrieve current data — callers use `reload`
 * on mount, on bucket change, and on tab re-focus so no browser refresh is needed.
 */
export default function useBucketData() {
  const [rawBuckets, setRawBuckets] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favourites, setFavourites] = useState(() => readLS(LS_FAVOURITES, []));
  const [favouriteBuckets, setFavouriteBuckets] = useState(() => readLS(LS_FAVOURITE_BUCKETS, []));

  const reload = useCallback(async (signal) => {
    setError(null);
    try {
      const res = await api.getPublisherBuckets(signal ? { signal } : {});
      const buckets = (res?.buckets || []).filter((b) => b.isActive !== false);
      setRawBuckets(buckets);
      setClients(groupByClient(buckets));
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
        setError(err?.response?.error || err?.message || 'Failed to load buckets');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  // Spec 2.5 / 6.1 — returning to the tab (e.g. after editing bucket config in
  // another tab) refreshes without requiring a manual browser reload.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    // `visibilitychange` does NOT fire for a tab that was visible the whole
    // time. Deactivate a bucket in the admin panel in a second WINDOW, side by
    // side with this one, and this tab never becomes hidden, so it kept showing
    // buckets the backend had already stopped returning until a manual browser
    // reload. `focus` covers that case; both are needed, because switching tabs
    // within one window fires visibilitychange without necessarily firing focus.
    const onFocus = () => reload();
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [reload]);

  const toggleFavourite = useCallback((clientId) => {
    setFavourites((prev) => {
      const next = prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId];
      writeLS(LS_FAVOURITES, next);
      return next;
    });
  }, []);

  // §2.2 — a bucket-level favourite adds a shortcut under Favourites; it does
  // NOT change the bucket, reorder Target buckets, or affect the publisher
  // accordion. Simply toggles membership in a per-admin list.
  const toggleFavouriteBucket = useCallback((bucketId) => {
    setFavouriteBuckets((prev) => {
      const next = prev.includes(bucketId)
        ? prev.filter((id) => id !== bucketId)
        : [...prev, bucketId];
      writeLS(LS_FAVOURITE_BUCKETS, next);
      return next;
    });
  }, []);

  return { rawBuckets, clients, loading, error, reload, favourites, toggleFavourite, favouriteBuckets, toggleFavouriteBucket };
}

// Spec 2.4 — Recent activity is the default order; A–Z is the only alternative.
// "Most used" / "Most pending" / "Most active suggestions" are explicitly excluded.
export function sortClients(clients, mode) {
  const arr = [...clients];
  if (mode === 'a-z') return arr.sort((a, b) => a.clientName.localeCompare(b.clientName));
  return arr.sort((a, b) => {
    const ra = a.metrics.lastActivityAt || 0;
    const rb = b.metrics.lastActivityAt || 0;
    if (ra !== rb) return rb - ra;
    return a.clientName.localeCompare(b.clientName);
  });
}

/**
 * The order every destination picker shows publishers in: favourites first,
 * then whatever sort the Publisher Buckets screen is set to.
 *
 * Shared rather than copied. Two pickers (Send to another bucket, Add to
 * bucket) read the same two localStorage keys, and a second copy of this is a
 * second chance for one of them to drift out of step with the panel the user
 * was just looking at. See CLAUDE.md gotcha 25.
 */
export function orderClientsForPicker(clients) {
  let sortMode = 'recent';
  let favourites = [];
  try {
    sortMode = window.localStorage.getItem('alchemiq_pbnew_sort') || 'recent';
    const raw = window.localStorage.getItem(LS_FAVOURITES);
    if (raw) favourites = JSON.parse(raw) || [];
  } catch { /* ignore, quota or private mode */ }
  const sorted = sortClients(clients, sortMode);
  const favSet = new Set(favourites.map(String));
  return [
    ...sorted.filter((c) => favSet.has(String(c.clientId))),
    ...sorted.filter((c) => !favSet.has(String(c.clientId))),
  ];
}
